// ============================================================
// CLUBHUB – Wiederholung: RRULE bauen, lesen, in Worte fassen
// ============================================================
// Gespeichert wird die Regel als RRULE nach RFC 5545 (`Event.rrule`)
// — dieselbe Sprache, die Apple, Google und Outlook im Kalender-Abo
// verstehen. Das Formular kennt nur den Ausschnitt, den ein Verein
// braucht: taeglich, woechentlich an Tagen, monatlich am Tag oder am
// n-ten Wochentag; Ende nie, an einem Datum oder nach n Terminen.
//
// Entfaltet (welche Tage es gibt) wird mit `rrule`, gerechnet in
// schwebender Ortszeit (`naivZuUtc`): DTSTART ohne TZID, UNTIL ohne
// „Z“. So steht das Training im Winter wie im Sommer um 19:00.
//
// In Worte fasst die Regel `regelKlartext` — selbst geschrieben,
// weil `rrule.toText()` nur Englisch kann.
// ============================================================
import { RRule } from "rrule";
import {
  WOCHENTAGE,
  addDays,
  dateFromDay,
  endOfMonth,
  formatTagKurz,
  formatTagMitJahr,
  naivZuUtc,
  tagVon,
  utcZuNaiv,
  weekdayIndex,
  type DayKey,
} from "./tag";

export const RRULE_TAGE = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

export type WiederholungsArt = "keine" | "taeglich" | "woechentlich" | "monatlich";

/** Was das Formular haelt. */
export type Wiederholung = {
  art: WiederholungsArt;
  intervall: number;
  /** 0 = Montag … 6 = Sonntag (nur woechentlich). */
  tage: number[];
  /** Monatlich am 14. oder am 2. Dienstag. */
  monatsweise: "tag" | "wochentag";
  ende: "nie" | "datum" | "anzahl";
  bis: DayKey;
  anzahl: number;
};

export function wiederholungVorgabe(start: DayKey): Wiederholung {
  return {
    art: "keine",
    intervall: 1,
    tage: [weekdayIndex(start)],
    monatsweise: "tag",
    ende: "datum",
    bis: addDays(start, 7 * 12),
    anzahl: 10,
  };
}

/** Der wievielte Wochentag im Monat ist `tag`? 5 heisst „letzter“ (-1). */
export function nterWochentag(tag: DayKey): number {
  const n = Math.ceil(Number(tag.slice(8, 10)) / 7);
  const letzter = addDays(tag, 7) > endOfMonth(tag);
  return letzter && n >= 4 ? -1 : n;
}

const ORDNUNG: Record<number, string> = { 1: "ersten", 2: "zweiten", 3: "dritten", 4: "vierten", [-1]: "letzten" };

function teile(rrule: string): Map<string, string> {
  const m = new Map<string, string>();
  for (const teil of rrule.replace(/^RRULE:/i, "").split(";")) {
    const [k, v] = teil.split("=");
    if (k && v) m.set(k.toUpperCase(), v.toUpperCase());
  }
  return m;
}

/** "20270331T235959" | "20270331" → "2027-03-31" */
function untilTag(wert: string): DayKey {
  return `${wert.slice(0, 4)}-${wert.slice(4, 6)}-${wert.slice(6, 8)}`;
}

/** Die Regel aus dem Formular — `null` ohne Wiederholung. */
export function regelBauen(w: Wiederholung, start: DayKey): string | null {
  if (w.art === "keine") return null;
  const teile: string[] = [];
  if (w.art === "taeglich") teile.push("FREQ=DAILY");
  if (w.art === "woechentlich") {
    teile.push("FREQ=WEEKLY");
    const tage = (w.tage.length ? w.tage : [weekdayIndex(start)]).slice().sort((a, b) => a - b);
    teile.push(`BYDAY=${tage.map((t) => RRULE_TAGE[t]).join(",")}`);
  }
  if (w.art === "monatlich") {
    teile.push("FREQ=MONTHLY");
    if (w.monatsweise === "tag") teile.push(`BYMONTHDAY=${Number(start.slice(8, 10))}`);
    else teile.push(`BYDAY=${nterWochentag(start)}${RRULE_TAGE[weekdayIndex(start)]}`);
  }
  if (w.intervall > 1) teile.push(`INTERVAL=${w.intervall}`);
  if (w.ende === "datum") teile.push(`UNTIL=${w.bis.replaceAll("-", "")}T235959`);
  if (w.ende === "anzahl") teile.push(`COUNT=${w.anzahl}`);
  return teile.join(";");
}

/** Die Regel fuer das Formular — Unbekanntes faellt auf die Vorgabe zurueck. */
export function regelLesen(rrule: string | null, start: DayKey): Wiederholung {
  const w = wiederholungVorgabe(start);
  if (!rrule) return w;
  const t = teile(rrule);
  const freq = t.get("FREQ");
  w.art = freq === "DAILY" ? "taeglich" : freq === "WEEKLY" ? "woechentlich" : freq === "MONTHLY" ? "monatlich" : "keine";
  w.intervall = Math.max(1, Number(t.get("INTERVAL") ?? 1));
  const byday = t.get("BYDAY");
  if (w.art === "woechentlich" && byday) {
    w.tage = byday
      .split(",")
      .map((d) => RRULE_TAGE.indexOf(d.slice(-2) as (typeof RRULE_TAGE)[number]))
      .filter((i) => i >= 0);
  }
  if (w.art === "monatlich") w.monatsweise = byday ? "wochentag" : "tag";
  const until = t.get("UNTIL");
  const count = t.get("COUNT");
  if (until) {
    w.ende = "datum";
    w.bis = untilTag(until);
  } else if (count) {
    w.ende = "anzahl";
    w.anzahl = Number(count);
  } else w.ende = "nie";
  return w;
}

function liste(woerter: string[]): string {
  if (woerter.length <= 1) return woerter.join("");
  return `${woerter.slice(0, -1).join(", ")} und ${woerter.at(-1)}`;
}

/**
 * Die Regel in Worten: „jeden Dienstag und Donnerstag bis 30.06.2027“,
 * „alle 2 Wochen am Montag“, „jeden ersten Freitag im Monat“.
 * Das Jahr am Ende steht nur, wenn es nicht das Jahr des Beginns ist.
 */
export function regelKlartext(rrule: string | null, start: DayKey): string {
  if (!rrule) return "einmalig";
  const t = teile(rrule);
  const freq = t.get("FREQ");
  const n = Math.max(1, Number(t.get("INTERVAL") ?? 1));
  let satz = "";

  if (freq === "DAILY") satz = n === 1 ? "täglich" : `alle ${n} Tage`;
  else if (freq === "WEEKLY") {
    const tage = (t.get("BYDAY") ?? RRULE_TAGE[weekdayIndex(start)]!)
      .split(",")
      .map((d) => RRULE_TAGE.indexOf(d.slice(-2) as (typeof RRULE_TAGE)[number]))
      .filter((i) => i >= 0)
      .sort((a, b) => a - b);
    const werktags = tage.length === 5 && tage.every((d, i) => d === i);
    if (tage.length === 7) satz = n === 1 ? "täglich" : `alle ${n} Wochen täglich`;
    else if (werktags) satz = n === 1 ? "werktags (Montag bis Freitag)" : `alle ${n} Wochen werktags`;
    else {
      const namen = liste(tage.map((d) => WOCHENTAGE[d]!));
      satz = n === 1 ? `jeden ${namen}` : `alle ${n} Wochen am ${namen}`;
    }
  } else if (freq === "MONTHLY") {
    const byday = t.get("BYDAY");
    if (byday) {
      const m = byday.match(/^([+-]?\d)?([A-Z]{2})$/);
      const nr = Number(m?.[1] ?? nterWochentag(start));
      const tag = WOCHENTAGE[RRULE_TAGE.indexOf((m?.[2] ?? "MO") as (typeof RRULE_TAGE)[number])] ?? "";
      satz = n === 1 ? `jeden ${ORDNUNG[nr] ?? `${nr}.`} ${tag} im Monat` : `alle ${n} Monate am ${ORDNUNG[nr] ?? `${nr}.`} ${tag}`;
    } else {
      const tag = Number(t.get("BYMONTHDAY") ?? start.slice(8, 10));
      satz = n === 1 ? `jeden Monat am ${tag}.` : `alle ${n} Monate am ${tag}.`;
    }
  } else if (freq === "YEARLY") satz = n === 1 ? "jährlich" : `alle ${n} Jahre`;
  else satz = "wiederkehrend";

  const until = t.get("UNTIL");
  const count = t.get("COUNT");
  if (until) {
    const bis = untilTag(until);
    satz += ` bis ${bis.slice(0, 4) === start.slice(0, 4) ? formatTagKurz(bis) : formatTagMitJahr(bis)}`;
  } else if (count) satz += `, insgesamt ${count}-mal`;
  return satz;
}

/**
 * Die Vorkommen einer Serie zwischen `von` und `bis` (beide Tage
 * eingeschlossen), als Beginn in schwebender Ortszeit.
 */
export function serieEntfalten(start: string, rrule: string, von: DayKey, bis: DayKey): string[] {
  const optionen = RRule.parseString(rrule);
  const regel = new RRule({ ...optionen, dtstart: naivZuUtc(start) });
  const ende = new Date(dateFromDay(addDays(bis, 1)).getTime() - 1000);
  return regel.between(dateFromDay(von), ende, true).map(utcZuNaiv);
}

/** Wie viele Vorkommen hat eine Serie hoechstens — fuer „insgesamt n Termine“ in der Vorschau. */
export function serieZaehlen(start: string, rrule: string, grenze = 500): number | null {
  const t = teile(rrule);
  if (!t.get("UNTIL") && !t.get("COUNT")) return null;
  const optionen = RRule.parseString(rrule);
  const regel = new RRule({ ...optionen, dtstart: naivZuUtc(start) });
  let n = 0;
  regel.all(() => ++n < grenze);
  return n;
}

/** Die naechsten Vorkommen ab `start` (fuer die Vorschau im Formular). */
export function ersteVorkommen(start: string, rrule: string, anzahl: number): string[] {
  const optionen = RRule.parseString(rrule);
  const regel = new RRule({ ...optionen, dtstart: naivZuUtc(start) });
  const ergebnis: string[] = [];
  regel.all((d, i) => {
    if (i >= anzahl) return false;
    ergebnis.push(utcZuNaiv(d));
    return true;
  });
  return ergebnis;
}

/** Der Tag eines Vorkommens als Schluessel in der Adresse. */
export function vorkommenTag(occurrence: string): DayKey {
  return tagVon(occurrence);
}

/** Die Erinnerungen, die das Formular anbietet (`Event.reminderMinutes`). */
export const ERINNERUNGEN = [
  { minuten: 60, label: "1 Stunde vorher" },
  { minuten: 180, label: "3 Stunden vorher" },
  { minuten: 1440, label: "1 Tag vorher" },
  { minuten: 2880, label: "2 Tage vorher" },
  { minuten: 10080, label: "1 Woche vorher" },
] as const;

/** „1 Tag vorher“, „90 Minuten vorher“ */
export function erinnerungText(minuten: number): string {
  const bekannt = ERINNERUNGEN.find((e) => e.minuten === minuten);
  if (bekannt) return bekannt.label;
  if (minuten % 1440 === 0) return `${minuten / 1440} Tage vorher`;
  if (minuten % 60 === 0) return `${minuten / 60} Stunden vorher`;
  return `${minuten} Minuten vorher`;
}
